import Joi from 'joi';

import type { ICreateLeadRequest } from 'src/services/api';
import { type ParsedCsv } from 'src/utils/csv';
import { isPossiblePhoneNumber } from 'libphonenumber-js';

export const importLeadsFields = [
    'name',
    'email',
    'phone_number',
    'ingestion_source',
    'property',
];

const NewLeadSchema = Joi.object<ICreateLeadRequest>({
    name: Joi.string().trim().required().max(100).messages({
        'string.max': '100 characters limit exceeded',
    }),
    email: Joi.string().trim().allow('').email({ tlds: false }),
    phone_number: Joi.string().trim().allow('').custom((value, helper) => {
        if (value === '') {
            return value;
        }
        if (!isPossiblePhoneNumber(value, 'US')) {
            return helper.error('any.invalid');
        }
        return value;
    }),
    ingestion_source: Joi.string().trim().required(),
    property: Joi.string().trim().allow(''),
}).unknown(true);

export type LeadCsvRow = {
    [K in keyof ICreateLeadRequest]: string;
};

function parseLead(row: LeadCsvRow) {
    // Normalize CSV headers, and remove any empty headers.
    const normalizedRow = Object.keys(row).reduce((acc, key) => {
        const trimmedKey = key.trim();
        if (!trimmedKey) {
            return acc;
        }
        acc[trimmedKey as keyof LeadCsvRow] = row[key as keyof LeadCsvRow];
        return acc;
    }, {} as Partial<LeadCsvRow>);

    const validationResult = NewLeadSchema.validate(normalizedRow, { abortEarly: false, convert: true });
    const trimmedRow = (validationResult.value ?? normalizedRow) as Partial<LeadCsvRow>;

    const lead: ICreateLeadRequest = {
        name: trimmedRow.name || '',
        email: trimmedRow.email || null,
        phone_number: trimmedRow.phone_number || null,
        ingestion_source: trimmedRow.ingestion_source || '',
        property: trimmedRow.property,
    };

    const missingContactInfo = !trimmedRow.email && !trimmedRow.phone_number;

    const errorKeys = [
        ...(validationResult.error?.details.map((o) => o.context?.key as string).filter(Boolean) ?? []),
        ...(missingContactInfo ? ['email', 'phone_number'] : []),
    ];

    const errorMsgs = [
        ...(validationResult.error?.details.map((o) => o.message) ?? []),
        ...(missingContactInfo ? ['Either email or phone number is required'] : []),
    ].filter(Boolean);

    const errors = errorKeys.length > 0 ? errorKeys : undefined;
    const errorMessages = errorMsgs.length > 0 ? errorMsgs : undefined;

    return {
        errors,
        errorMessages,
        lead,
    };
}

export function parseLeads(parsedCsv: ParsedCsv<LeadCsvRow>) {
    return parsedCsv.body.map(parseLead);
}

export const getUnknownLeadFields = (parsedCsv: ParsedCsv<LeadCsvRow>) => {
    const unknownFields: string[] = [];

    parsedCsv.header.forEach((header) => {
        const headerText = String(header ?? '').trim();
        if (!headerText) {
            return;
        }
        if (!importLeadsFields.includes(headerText)) {
            unknownFields.push(headerText);
        }
    });

    return unknownFields;
};
